import { useCallback, useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Camera, Plus, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/ModulePage";
import { Button } from "../components/ui/button";
import { ChecklistEditor } from "../components/ChecklistEditor";

const MODULE_ORDER = [["fire", "Fire safety"], ["puwer", "Machine safety / PUWER"], ["welfare", "Worker welfare"], ["first_aid", "First aid"], ["ladders", "Ladders"], ["legionella", "Legionella"], ["coshh", "COSHH"]];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const GroupEditor = ({ group, onSaved, canEdit }) => {
  const [qs, setQs] = useState(group.questions);
  const [open, setOpen] = useState(false);
  const tid = `qlib-${slug(group.key)}`;
  useEffect(() => { setQs(group.questions); }, [group]);
  const upd = (i, patch) => setQs(qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const move = (i, d) => { const a = [...qs]; [a[i], a[i + d]] = [a[i + d], a[i]]; setQs(a); };
  const save = async () => {
    if (qs.some((q) => !q.text.trim())) return toast.error("Fill in or delete the blank question first");
    try {
      await api.put(`/question-bank/${encodeURIComponent(group.key)}`, { questions: qs });
      toast.success(`${group.label} questions saved`);
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const reset = async () => {
    if (!window.confirm(`Reset ${group.label} to the standard questions?`)) return;
    try {
      await api.delete(`/question-bank/${encodeURIComponent(group.key)}`);
      toast.success("Reset to standard questions");
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="panel" data-testid={tid}>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left" data-testid={`${tid}-toggle`}>
        <span>
          <span className="font-semibold">{group.label}</span>
          <span className="block text-xs text-slate-400">{group.questions.length} questions - {group.questions.filter((q) => q.photo).length} need a photo{group.customised ? " - customised" : ""}</span>
        </span>
        <span className="eyebrow text-[10px]">{open ? "Close" : canEdit ? "Edit" : "View"}</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-800 p-4">
          {qs.map((q, i) => (
            <div key={i} className="flex items-center gap-1.5" data-testid={`${tid}-row-${i}`}>
              <input className="field h-10" disabled={!canEdit} value={q.text} onChange={(e) => upd(i, { text: e.target.value })} data-testid={`${tid}-text-${i}`} />
              <button type="button" disabled={!canEdit} title="Photo always required" onClick={() => upd(i, { photo: !q.photo })} data-testid={`${tid}-photo-${i}`}
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md border ${q.photo ? "border-amber-400 bg-amber-500/20 text-amber-200" : "border-slate-700 text-slate-500"}`}><Camera className="h-4 w-4" /></button>
              {canEdit && (
                <>
                  <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-1 disabled:opacity-30" data-testid={`${tid}-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
                  <button type="button" disabled={i === qs.length - 1} onClick={() => move(i, 1)} className="p-1 disabled:opacity-30" data-testid={`${tid}-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
                  <button type="button" onClick={() => setQs(qs.filter((_, j) => j !== i))} className="p-1 text-red-300" data-testid={`${tid}-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
          ))}
          {canEdit && (
            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="button" variant="outline" className="border-slate-700 bg-transparent" onClick={() => setQs([...qs, { text: "", photo: false }])} data-testid={`${tid}-add`}><Plus className="mr-1 h-4 w-4" />Add question</Button>
              <Button type="button" className="bg-rose-600 hover:bg-rose-700" onClick={save} data-testid={`${tid}-save`}>Save</Button>
              {group.customised && <Button type="button" variant="ghost" onClick={reset} data-testid={`${tid}-reset`}><RotateCcw className="mr-1 h-4 w-4" />Reset to standard</Button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function QuestionLibrary() {
  const { isManager } = useAuth();
  const [groups, setGroups] = useState([]);
  const [tpls, setTpls] = useState({});
  const load = useCallback(() => api.get("/question-bank").then((r) => setGroups(r.data.groups)), []);
  const loadTpls = useCallback(() => api.get("/checklists").then((r) => { setTpls(r.data); localStorage.setItem("eps_checklists", JSON.stringify(r.data)); }), []);
  useEffect(() => { loadTpls(); }, [loadTpls]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mx-auto max-w-4xl" data-testid="question-library-page">
      <PageHeader eyebrow="Pathway audits" title="Question Library"
        description="The standard questions asked for each asset type and for every area on a pathway audit, plus every module inspection checklist (scroll down). Edit wording, reorder, add or remove questions, and tap the camera to make a photo mandatory. Changes apply to all pathways that use the standard questions; questions customised in the pathway builder stay as set there." />
      {["Area", "Asset type", "Asset category"].map((scope) => (
        <div key={scope} className="mb-6">
          <h2 className="font-display mb-2 text-xl font-bold uppercase">{scope === "Area" ? "Every area" : scope === "Asset type" ? "By asset type" : "By asset category (used when no type-specific list)"}</h2>
          <div className="space-y-2">
            {groups.filter((g) => g.scope === scope).map((g) => <GroupEditor key={g.key} group={g} onSaved={load} canEdit={isManager} />)}
          </div>
        </div>
      ))}
      <div className="mb-6" data-testid="checklist-library">
        <h2 className="font-display mb-1 text-xl font-bold uppercase">Inspection checklists</h2>
        <p className="mb-3 text-sm text-slate-400">Applies to every site. Tap the camera to make a photo mandatory, or the thermometer to record a reading with limits. Past inspections keep the questions they were completed with.</p>
        {MODULE_ORDER.map(([m, label]) => {
          const list = Object.entries(tpls).filter(([, t]) => t.module === m);
          return list.length ? (
            <div key={m} className="mb-4" data-testid={`checklist-module-${m}`}>
              <p className="eyebrow mb-2">{label}</p>
              <div className="space-y-2">{list.map(([k, t]) => <ChecklistEditor key={k} tkey={k} tpl={t} canEdit={isManager} onSaved={loadTpls} />)}</div>
            </div>
          ) : null;
        })}
      </div>
    </div>
  );
}
